import React, { useState } from 'react';
import { styled, Flex, Col, Title } from '@teko/ds-mobile';
import dynamic from 'next/dynamic';
import { t } from 'i18n';
import { usePos365StoreCode, useTenantConfig } from 'hooks';
import { convertDiscoveryFilter, convertToPayloadSorting } from 'helpers';
import { cleanObject } from '@teko/utils';
import { FilterOption } from '@teko/types/ecommerce';
import {
  SortOptionInterface,
  cookFilterPrice,
  extractSortQuery,
} from 'modules/search-page/shared';
import { useBuildPcProducts } from 'hooks';
import { convertFilter } from 'helpers';
import {
  BUILD_PC_MODAL_PAGE_SIZE,
  DEFAULT_PRICE_FILTER,
  ITEM_PER_PAGE_IN_LISTING_PAGE,
} from 'constants/index';
import { BuildPCFilterMobile } from 'components/mobile/organisms/BuildPC/BuildPCFilterMobile';
import { Pagination } from '@teko/ds-desktop';

const ProductList = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductList),
);

const ProductGrid = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductGrid),
);

const NotFound = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.NoProductsFound),
);

type ProductCardType = 'grid' | 'list';

interface BuildPcModalComponentProps {
  collectionSlug: string;
  categoryName: string;
  keyword: string;
  currentPage: number;
  onPageChange?: (page: number) => void;
  onClose: () => void;
}

export const BuildPcModalComponent: React.FC<BuildPcModalComponentProps> = (
  props,
) => {
  const {
    collectionSlug,
    keyword,
    currentPage,
    categoryName,
    onPageChange,
    onClose,
  } = props;

  const [priceFilterValue, setPriceFilterValue] = useState<{
    gte: number;
    lte: number;
  }>();
  const [productCardType, setProductCardType] =
    useState<ProductCardType>('grid');

  const { pos365StoreCode } = usePos365StoreCode();
  const { showCollectionPageTitle, contactLink } = useTenantConfig();

  const [activeOptions, setActiveOptions] = useState<FilterOption[]>([]);
  const [activeSortOption, setActiveSortOption] = useState<
    SortOptionInterface | undefined
  >();

  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];

  const { filter, loading: filterLoading } = useBuildPcProducts(
    {
      slug: collectionSlug,
      returnFilterable: [
        'FILTER_TYPE_ATTRIBUTE',
        'FILTER_TYPE_BRAND',
        'FILTER_TYPE_PRICE',
        'FILTER_TYPE_CLEARANCE',
      ],
      pageSize: 1,
    },
    onClose,
    categoryName,
  );

  const filters = !!filter ? convertDiscoveryFilter(filter) : [];
  const priceFilter = cookFilterPrice(filter?.priceLte, filter?.priceGte);

  const onSortOptionSelected = (sortOption: SortOptionInterface) => {
    if (sortOption.optionCode === activeSortOption?.optionCode) {
      setActiveSortOption(undefined);
    } else {
      setActiveSortOption(sortOption);
    }
  };

  const onSubmit = (
    activeFilters: FilterOption[],
    filterPriceValue: {
      gte: number;
      lte: number;
    },
  ) => {
    setActiveOptions(activeFilters);
    setPriceFilterValue(filterPriceValue);
  };

  const onClearFilters = () =>
    onSubmit([], {
      gte: priceFilter.min,
      lte: priceFilter.max,
    });

  const {
    products,
    pagination,
    loading: isLoading,
  } = useBuildPcProducts(
    {
      slug: collectionSlug,
      query: keyword,
      sorting: convertToPayloadSorting(sort, order),
      filter: cleanObject({
        ...convertFilter(activeOptions),
        priceGte: priceFilterValue?.gte,
        priceLte: priceFilterValue?.lte,
      }),
      pageSize: BUILD_PC_MODAL_PAGE_SIZE,
      page: currentPage,
    },
    onClose,
    categoryName,
  );

  return (
    <>
      {!!showCollectionPageTitle ? (
        <PageTitleContainer justify="start" align="center">
          <Col style={{ maxWidth: '90%' }}>
            <PageTitle>{t('Chọn linh kiện')}</PageTitle>
          </Col>
          {pagination?.totalItems != undefined ? (
            <Col>
              <Title
                color="textSecondary"
                weight="regular"
                style={{ fontSize: '0.75rem', lineHeight: '0.75rem' }}
              >
                {t('({{quantity}} sản phẩm)', {
                  quantity: pagination?.totalItems,
                })}
              </Title>
            </Col>
          ) : null}
        </PageTitleContainer>
      ) : null}
      <BuildPCFilterMobile
        loading={filterLoading}
        onClickLayout={(): void =>
          setProductCardType(productCardType === 'grid' ? 'list' : 'grid')
        }
        layoutType={pos365StoreCode ? 'list' : productCardType}
        filters={filters}
        filterPrice={{
          ...priceFilter,
          value: priceFilterValue || DEFAULT_PRICE_FILTER,
        }}
        activeSortOption={activeSortOption}
        activeOptions={activeOptions}
        onSortOptionSelected={onSortOptionSelected}
        onSubmit={onSubmit}
        onClearFilters={onClearFilters}
      />
      {pos365StoreCode ? (
        <StyledProductList
          products={products}
          contactLink={contactLink}
          itemsPerPage={
            !!pagination
              ? Math.ceil(pagination?.totalItems / pagination?.totalPages)
              : ITEM_PER_PAGE_IN_LISTING_PAGE
          }
        />
      ) : (
        <StyledProductGrid
          type={productCardType}
          products={products}
          contactLink={contactLink}
          productsPerRow={2}
          itemsPerPage={
            !!pagination
              ? Math.ceil(pagination?.totalItems / pagination?.totalPages)
              : ITEM_PER_PAGE_IN_LISTING_PAGE
          }
        />
      )}

      {!isLoading && products?.length === 0 && <NotFound show />}
      {!!pagination ? (
        <StyledFooter justify="center" align="center">
          <StyledPagination
            height="50px"
            pageNeighbor={1}
            totalRecords={pagination?.totalItems}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        </StyledFooter>
      ) : null}
    </>
  );
};

const PageTitleContainer = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background};
  padding: 0.5rem 0.75rem;
  position: sticky;
  top: 0px;
  z-index: 1040;
  height: 40px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1rem;
  line-height: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledFooter = styled(Flex)`
  position: sticky;
  padding: 0 16px;
  bottom: 0px;
  background-color: white;
`;

const StyledPagination = styled(Pagination)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  > :first-child {
    width: 100%;
    justify-content: center;
  }
`;

const StyledProductGrid = styled(ProductGrid)`
  height: calc(
    100% - 40px - 40px - 50px
  ); // minus the height of title container, filters & pagination
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledProductList = styled(ProductList)`
  height: calc(
    100% - 40px - 40px - 50px
  ); // minus the height of title container, filters & pagination
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;
